import { create } from 'zustand';
import { profileService } from '@/services/profile.service';
import { authService } from '@/services/auth.service';
import { StorageService } from '@/services/storage.service';

type Theme = 'light' | 'dark';

interface ThemeState {
  theme: Theme;
  loading: boolean;
  initialized: boolean;
  toggleTheme: () => Promise<void>;
  setTheme: (theme: Theme) => Promise<void>;
  initializeTheme: (userThemePreference?: 'light' | 'dark' | null) => void;
}

// Apply theme to DOM
const applyTheme = (theme: Theme) => {
  if (typeof window === 'undefined') return;
  
  const root = document.documentElement;
  if (theme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
};

// Get theme from localStorage (fallback para quando não há usuário ou banco não tem preferência)
const getThemeFromStorage = (): Theme => {
  if (typeof window === 'undefined') return 'light';
  const savedTheme = localStorage.getItem('theme') as Theme | null;
  if (savedTheme === 'light' || savedTheme === 'dark') {
    return savedTheme;
  }
  return 'light'; // Padrão: light
};

// Get initial theme: user preference from DB > localStorage > default 'light'
const getInitialTheme = (userThemePreference?: 'light' | 'dark' | null): Theme => {
  // Se o usuário tem preferência salva no banco (mesmo que seja 'light'), usa ela
  if (userThemePreference === 'light' || userThemePreference === 'dark') {
    return userThemePreference;
  }
  
  // Senão (se for undefined ou null), usa do localStorage ou padrão 'light'
  return getThemeFromStorage();
};

export const useThemeStore = create<ThemeState>((set) => ({
  theme: 'light', // Valor inicial temporário, será substituído quando initializeTheme for chamado
  loading: false,
  initialized: false,

  initializeTheme: (userThemePreference?: 'light' | 'dark' | null) => {
    // Só inicializa uma vez para evitar sobrescritas
    const state = useThemeStore.getState();
    if (state.initialized) {
      return; // Já foi inicializado, não sobrescrever
    }
    
    const theme = getInitialTheme(userThemePreference);
    applyTheme(theme);
    // Salvar no localStorage para facilitar acesso futuro
    if (typeof window !== 'undefined') {
      localStorage.setItem('theme', theme);
    }
    set({ theme, initialized: true });
  },

  toggleTheme: async () => {
    set((state) => {
      const newTheme = state.theme === 'light' ? 'dark' : 'light';
      
      // Aplicar tema imediatamente
      applyTheme(newTheme);
      
      // Salvar no localStorage para facilitar acesso
      if (typeof window !== 'undefined') {
        localStorage.setItem('theme', newTheme);
      }
      
      // Salvar no banco se houver usuário logado
      const currentUser = authService.getCurrentUser();
      if (currentUser?.user) {
        // Salvar no banco de forma assíncrona (não bloquear UI)
        profileService.updateProfile({ themePreference: newTheme }).then((authResponse) => {
          // Atualizar tokens e usuário no storage local
          StorageService.setUser(authResponse.user);
          StorageService.setAccessToken(authResponse.accessToken);
          StorageService.setRefreshToken(authResponse.refreshToken);
        }).catch((err) => {
          console.error('Erro ao salvar preferência de tema:', err);
        });
      }
      
      return { theme: newTheme };
    });
  },

  setTheme: async (theme: Theme) => {
    // Aplicar tema imediatamente
    applyTheme(theme);
    
    // Salvar no localStorage para facilitar acesso
    if (typeof window !== 'undefined') {
      localStorage.setItem('theme', theme);
    }
    
    set({ theme });
    
    // Salvar no banco se houver usuário logado
    const currentUser = authService.getCurrentUser();
    if (currentUser?.user) {
      // Salvar no banco de forma assíncrona (não bloquear UI)
      profileService.updateProfile({ themePreference: theme }).then((authResponse) => {
        // Atualizar tokens e usuário no storage local
        StorageService.setUser(authResponse.user);
        StorageService.setAccessToken(authResponse.accessToken);
        StorageService.setRefreshToken(authResponse.refreshToken);
      }).catch((err) => {
        console.error('Erro ao salvar preferência de tema:', err);
      });
    }
  },
}));

// Não inicializar tema aqui - será inicializado pelo App.tsx quando o perfil do usuário for carregado
// Isso garante que o tema do banco seja aplicado corretamente
